import React, {useState} from 'react';
import '../../styles/calculator.scss';
import {Box, CircularProgress, Fade, Grid, TextField, Typography, useMediaQuery} from "@mui/material";
import {theme} from "../../styles/theme";
import {DimensionsUnit, WeightUnit} from "../../models";
import {Form, Formik, FormikProps} from "formik";
import * as Yup from 'yup';
import {t} from "i18next";
import {getQuoteParcels} from "../common/api";
import ZipInputFormik from "./formik/zip-input";
import CountryInputFormik from "./formik/country-input";
import WeightInputFormik from "./formik/weight-input";
import DeliveryMethodsListParcels from "../delivery-methods-list-parcels";
import DimensionsInputFormik from "./formik/dimensions-input";

const defaultOriginCountry = {
    countryCode: "US",
    name: "United States of America"
}

const WEIGHT_LIMIT_C2C = "50.00";
const WEIGHT_LIMIT_C2C_UNIT = WeightUnit.Kg;

export type CalculatorC2CValues = {
    originCountryCode: string | null;
    originZip: string | null;
    destinationCountryCode: string | null;
    destinationZip: string | null;
    weight: number | null;
    weightUnit: WeightUnit | null;
    length: string | null;
    width: string | null;
    height: string | null;
    dimensionsUnit: DimensionsUnit | null;
}

const initialValues: CalculatorC2CValues = {
    originCountryCode: "US",
    originZip: null,
    destinationCountryCode: null,
    destinationZip: null,
    weight: null,
    weightUnit: WeightUnit.Lb,
    length: null,
    width: null,
    height: null,
    dimensionsUnit: DimensionsUnit.In,
}

const isNumber = (value: string | undefined) => {
    const num = Number(value)
    return (num && num >= 0.001) || !value;
}

const isDimensionsValid = ({length, width, height}: any) => {
    const anyProvided = length || width || height;
    const allProvided = length && width && height;

    return !anyProvided || allProvided;
}

const validationSchema = Yup.object().shape({
    originCountryCode: Yup.string().required(),
    originZip: Yup.string().required(),
    destinationCountryCode: Yup.string().required(),
    weight: Yup.string()
        .test((value) => isNumber(value))
        .required(),
    weightUnit: Yup.string().required(),
    length: Yup.string()
        .test((value) => isNumber(value))
        .test((value, context) =>
            isDimensionsValid({length: value, width: context?.parent?.width, height: context?.parent?.height}))
        .nullable(),
    width: Yup.string()
        .test((value) => isNumber(value))
        .test((value, context) =>
            isDimensionsValid({length: context?.parent?.length, width: value, height: context?.parent?.height}))
        .nullable(),
    height: Yup.string()
        .test((value) => isNumber(value))
        .test((value, context) =>
            isDimensionsValid({length: context?.parent?.length, width: context?.parent?.width, height: value}))
        .nullable()
});

function CalculatorC2C() {

    const [weight, setWeight] = useState<string | null>("");

    const [deliveryMethods, setDeliveryMethods] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false)
    const [sort, setSort] = useState(false)
    const [isNothingFound, setIsNothingFound] = useState(false)

    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

    const handleSubmit = (values: CalculatorC2CValues) => {

            const providedWeight = weight ? Number(weight) : 1;
            if (!weight)
                setWeight("1")

            if (providedWeight < 0.001)
                return;

            setIsNothingFound(false)
            setIsLoading(true)

            getQuoteParcels({
                ...values,
                originZip: values.originZip ?? "",
                destinationCountryCode: values.destinationCountryCode ?? "",
                weight: values.weight ?? 1,
                weightUnit: values.weightUnit ?? WeightUnit.Kg,
                length: values.length ?? "0",
                width: values.width ?? "0",
                height: values.height ?? "0"
            }).then((response: any) => {
                let results: any[] = [];
                response?.data?.outputs?.quoteMatrix?.map((route: any) => {
                    route?.rates?.map((result: any) => {
                        results.push(result);
                    });
                });

                if (results.length === 0) {
                    setIsNothingFound(true)
                }
                setDeliveryMethods(results)

            }).catch((error) => console.log(error))
                .finally(() => setIsLoading(false))
        }
    ;

    const handleWeightChange = (_: FormikProps<CalculatorC2CValues>) => {
        if (deliveryMethods?.length) {
            setDeliveryMethods([])
        }
    }

    const handleDestinationCountryChange = (_: FormikProps<CalculatorC2CValues>) => {
        if (deliveryMethods?.length) {
            setDeliveryMethods([])
        }
    }

    return (
        <Grid className={'calculator'}>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
            >
                <Form>
                    <Grid display={"flex"} flexDirection={"column"} padding={3} gap={2} borderRadius={2}
                          className={"container"}
                          sx={{
                              backgroundColor:
                                  "var(--color-content-link)"
                          }}>
                        <Grid display={"flex"} alignItems={"center"} container columns={{md: 13, sm: 2, xs: 1}}
                              gap={{md: 0, sm: 0, xs: 2}}>
                            <Grid item md={1.5} sm={2} xs={1} paddingBottom={{md: 0, sm: 1}}
                                  paddingRight={{md: "0.75rem"}}>
                                <Typography component={"div"} className={"h3"} fontFamily={"Manrope, sans-serif"} fontWeight={700}
                                            fontSize={"max( 1.5rem,min( 2vw ,2.2rem ) )"}
                                            color={"var(--color-content-bg)!important"}>From:</Typography>
                            </Grid>

                            <Grid item md={7.5} sm={1} xs={1} paddingX={{md: "0.75rem"}} paddingRight={{sm: "0.5rem"}}>
                                <TextField value={defaultOriginCountry.name} fullWidth InputProps={{disabled: true}}/>
                            </Grid>
                            <Grid item md={4} sm={1} xs={1} paddingLeft={{md: "0.75rem", sm: "0.5rem"}}>
                                <ZipInputFormik fieldName={"originZip"}
                                                placeholder={t("zip.placeholder")}/>
                            </Grid>
                        </Grid>
                        <Grid display={"flex"} alignItems={"center"} container columns={{md: 13, sm: 2, xs: 1}}
                              gap={{md: 0, sm: 0, xs: 2}}>
                            <Grid md={1.5} sm={2} xs={1} paddingBottom={{md: 0, sm: 1}} paddingRight={{md: "0.5rem"}}>
                                <Typography component={"div"} className={"h3"} fontFamily={"Manrope, sans-serif"} fontWeight={700}
                                            fontSize={"max( 1.5rem,min( 2vw ,2.2rem ) )"}
                                            color={"var(--color-content-bg)!important"}>To:</Typography>
                            </Grid>
                            <Grid item md={7.5} sm={1} xs={1} paddingX={{md: "0.75rem"}} paddingRight={{sm: "0.5rem"}}>
                                <CountryInputFormik fieldName={"destinationCountryCode"}
                                                    onChange={handleDestinationCountryChange}/>
                            </Grid>

                            <Grid item md={4} sm={1} xs={1} paddingLeft={{md: "0.75rem", sm: "0.5rem"}}>
                                <ZipInputFormik fieldName={"destinationZip"}
                                                placeholder={t("postalCode.placeholder")}/>
                            </Grid>
                        </Grid>
                        <Grid display={"flex"} alignItems={"center"} container columns={{md: 13, sm: 2, xs: 1}}
                              gap={{md: 0, sm: 2, xs: 2}}>
                            <Grid item md={1.5} sm={2} xs={1} paddingBottom={{md: 0, sm: 1}}
                                  paddingRight={{md: "0.5rem"}}>
                                <Typography component={"div"} className={"h3"} fontFamily={"Manrope, sans-serif"} fontWeight={700}
                                            fontSize={"max( 1.5rem,min( 2vw ,2.2rem ) )"}
                                            color={"var(--color-content-bg)!important"}>Parcel:</Typography>
                            </Grid>
                            <Grid item md={7.5} sm={2} xs={1} display={"flex"}
                                  alignItems={"center"}
                                  flexDirection={isDesktop ? "row" : "column"}
                                  paddingX={{md: "0.75rem"}}
                                  gap={{md: 0, sm: 1, xs: 1}}>
                                <DimensionsInputFormik/>
                            </Grid>
                            <Grid item md={4} sm={2} xs={1} display={"flex"}
                                  paddingLeft={{md: "0.75rem", sm: 0}}>
                                <WeightInputFormik units={[WeightUnit.Lb]} onChange={handleWeightChange}
                                                   weightLimit={WEIGHT_LIMIT_C2C}
                                                   weightLimitUnit={WEIGHT_LIMIT_C2C_UNIT}/>
                            </Grid>
                        </Grid>
                        <Grid display={"flex"} alignItems={"center"} justifyContent={"flex-end"} container
                              columns={{md: 13, sm: 2, xs: 1}}>
                            <Grid md={4} sm={2} xs={1} paddingTop={{md: 0, sm: 2}} paddingLeft={{md: "0.75rem"}}>
                                <button type={"submit"} className="w-btn us-btn-style_1"
                                        style={isDesktop ? {height: '48px', width: "100%"} : {
                                            width: "100%",
                                            height: '48px'
                                        }}
                                        disabled={isLoading}>
                        <span className="w-btn-label"
                              style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{isLoading ?
                            <CircularProgress sx={{height: '20px', width: '20px'}}
                                              size={'sm'}/> : t('calculate')}</span>
                                </button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Form>
            </Formik>
            <Fade in={!isLoading}>
                {deliveryMethods?.length > 0 ? <div className={'calculator-results'}>
                    <div style={{
                        display: 'flex',
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingTop: "60px",
                        paddingBottom: "48px",
                        flexWrap: "wrap",
                        rowGap: "20px"
                    }}>
                        <div className={"h3"}>{t('deliveryMethods.title')}</div>
                        {deliveryMethods?.length > 1 ? <div className={'calculator-results-sort'} onClick={() => {
                            setSort(!sort)
                        }}><div className={"h3"} style={{
                            fontWeight: 600,
                            fontSize: "1rem"
                        }}>{sort ? t('deliveryMethods.sort.max') : t('deliveryMethods.sort.min')}</div>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transform: sort ? 'rotate(180deg)' : '',
                                    marginLeft: "8px"
                                }}>
                                <img
                                    src={"https://trtshopping.com/wp-content/uploads/2023/06/dropdown-icon.svg"}
                                    alt={''}
                                    width="12"/>
                            </Box>
                        </div> : null}
                    </div>
                    <DeliveryMethodsListParcels deliveryMethods={deliveryMethods} weight={weight}
                                                weightUnit={WeightUnit.Lb}
                                                sort={sort}/>

                </div> : isNothingFound ?
                    <div className={'calculator-results-nothing-found'}>
                        <div className={'icon-box'}>
                            <img
                                width="16" src={'https://trtshopping.com/wp-content/uploads/2023/06/Icon.svg'}
                                alt={""}/>
                        </div>
                        <div className={"h3"}>{t('deliveryMethods.nothingFound.title')}</div>
                        <div className={"h4"}>{t('deliveryMethods.nothingFound.description1')}<br/>{t('deliveryMethods.nothingFound.description2')}
                        </div>
                    </div> : <div></div>
                }
            </Fade>
        </Grid>
    );
}

export default CalculatorC2C;
